import {AgendaV3} from "crqlar-react-common/dist/models/agenda/agenda.model";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    FormControl,
    MenuItem,
    Select,
    Stack
} from "@mui/material";
import {getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import LocalizedTextField
    from "crqlar-react-common/dist/components/LocalizedTextField";
import {ArrowDownward} from "@mui/icons-material";
import * as React from "react";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";

interface Props {
    agenda: AgendaV3
    updateField: (fields: Partial<AgendaV3>) => void
}

const defaultCustomLocalizations: Record<string, string> = {
    en: `{
  "Title": "Weekly Planner",
  "Participants": "Participants",
  "Seats Remaining": "Seats Remaining",
  "Close": "Close",
  "Book now": "Book now",
  "Reservation": "Reservation",
  "Reservations": "Reservations",
  "No reservations yet": "No reservations yet",
  "Cancel booking": "Cancel booking",
  "This event has already started": "This event has already started",
  "This event is fully booked": "This event is fully booked"
}`,
    de: `{
  "Title": "Wochenprogramm",
  "Participants": "Teilnehmer",
  "Seats Remaining": "Verbleibende Plätze",
  "Close": "Schließen",
  "Book now": "Jetzt buchen",
  "Reservation": "Reservierung",
  "Reservations": "Reservierungen",
  "No reservations yet": "Noch keine Reservierungen",
  "Cancel booking": "Buchung stornieren",
  "This event has already started": "Diese Veranstaltung hat bereits begonnen",
  "This event is fully booked": "Diese Veranstaltung ist ausgebucht"
}`
}

const AgendaDetailsLocalizationTab: React.FC<Props> = ({agenda, updateField}) => {
    const {t} = useLocalize()

    const customLocalizations = agenda.uiConfig?.customLocalizations ?? defaultCustomLocalizations;

    let translationsValid = true
    try {
        JSON.parse(customLocalizations['en'] || '{}')
        JSON.parse(customLocalizations['de'] || '{}')
        JSON.parse(customLocalizations['it'] || '{}')
    } catch {
        translationsValid = false
    }

    const defaultLanguageCode = agenda.uiConfig?.defaultLanguageCode ?? '';

    const localizationsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Localization')}
            </Typography>

            <Stack gap={1}>
            <Property
                title={t('Default language')}
                tooltip={t('Fallback language used when the visitor language cannot be detected or a translation is missing.')}
            >
                <FormControl fullWidth>
                    <Select
                        value={defaultLanguageCode}
                        onChange={(e) =>
                            updateField({
                                uiConfig: {
                                    ...agenda.uiConfig,
                                    defaultLanguageCode: (e.target.value as string) || undefined
                                }
                            })
                        }
                        size={'small'}
                        displayEmpty
                    >
                        <MenuItem value="">
                            {t('Auto-detect')}
                        </MenuItem>
                        {['en', 'de', 'it'].map((languageCode) => (
                            <MenuItem key={languageCode} value={languageCode}>
                                {getNameForLanguage(languageCode)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Property>

            <Property
                title={t('Custom localizations')}
                tooltip={t('JSON translations for agenda widget texts, grouped by language code.')}
            >
                <Stack sx={{width: '100%', gap: 1}}>
                    <LocalizedTextField
                        multiline
                        rows={10}
                        value={customLocalizations}
                        onChange={(val) =>
                            updateField({
                                uiConfig: {
                                    ...agenda.uiConfig,
                                    customLocalizations: val
                                }
                            })
                        }
                        fullWidth
                        required
                    />

                    {!translationsValid && (
                        <Alert
                            variant={'outlined'}
                            severity={'warning'}
                            sx={{py: 0, lineHeight: 1, alignItems: 'center'}}
                        >
                            {t('Custom translations JSON format is not valid')}
                        </Alert>
                    )}

                    <Accordion>
                        <AccordionSummary expandIcon={<ArrowDownward/>}>
                            {t('All translatable texts')}
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '0.8rem'
                            }}>
                                {customLocalizations.en ?? defaultCustomLocalizations.en}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Stack>
            </Property>
            </Stack>
        </Stack>
    )

    return (
        <Stack gap={6}>
            {localizationsSection}
        </Stack>
    )
}

export default AgendaDetailsLocalizationTab;
